import { SearchQueryProvider } from 'components/search/search.context';
import { useSearchQuery } from 'components/search/search.hooks';
import {
  useListMarketingSMSProgramsQuery,
  useListOrderConfirmationsQuery,
  useListGuestCheckoutVerificationsQuery,
  useListAccountVerificationsQuery,
  useListPasswordlessAuthenticationsQuery,
} from 'api/sms-reports-service/sms-reports-service';
import { ReactNode, useMemo } from 'react';
import { useIncPageTabPanelsContext } from 'components/inc-page/inc-page-tab-panels/inc-page-tab-panels.context';

type SMSReportsSearchProps = {
  children: ReactNode;
};

const SMSReportsSearch = ({ children }: SMSReportsSearchProps) => {
  const { tabs, tabIndex } = useIncPageTabPanelsContext();
  const section = useMemo(() => tabs[tabIndex]?.path, [tabIndex, tabs]);

  const marketingData = useSearchQuery(useListMarketingSMSProgramsQuery, {
    filteredSearchParams: ['storeIds'],
  });
  const orderConfirmationData = useSearchQuery(useListOrderConfirmationsQuery, {
    filteredSearchParams: ['count', 'page'],
    getIsSkipToken: data => data.storeIds.value.length === 0,
  });
  const accountVerificationData = useSearchQuery(
    useListAccountVerificationsQuery,
    {
      filteredSearchParams: ['storeIds', 'count', 'page'],
    },
  );
  const guestCheckoutVerificationData = useSearchQuery(
    useListGuestCheckoutVerificationsQuery,
    {
      filteredSearchParams: ['count', 'page'],
      getIsSkipToken: data => data.storeIds.value.length === 0,
    },
  );
  const passwordlessAuthenticationData = useSearchQuery(
    useListPasswordlessAuthenticationsQuery,
    {
      filteredSearchParams: ['storeIds', 'count', 'page'],
    },
  );

  const sectionDataMapping = useMemo(() => {
    return {
      marketing: { data: marketingData },
      orderconfirmation: { data: orderConfirmationData },
      accountverification: { data: accountVerificationData },
      guestcheckoutverification: { data: guestCheckoutVerificationData },
      passwordlessauthentication: { data: passwordlessAuthenticationData },
    };
  }, [
    accountVerificationData,
    marketingData,
    orderConfirmationData,
    guestCheckoutVerificationData,
    passwordlessAuthenticationData,
  ]);

  const sectionData = sectionDataMapping[section] || { data: null };
  const { data } = sectionData;

  return (
    <SearchQueryProvider key={section} value={data}>
      {children}
    </SearchQueryProvider>
  );
};

export default SMSReportsSearch;
